import { IPromise, IQService, IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { buildAssigneesSearchRequests, searchItemsToAssignees } from "@gtmhub/assignees/utils/search-utils";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator } from "@gtmhub/error-handling";
import { SearchService } from "@gtmhub/search/services/search.service";
import { SnackbarEvents } from "@gtmhub/shared/components/ui/snackbar/snackbar.events";
import { INgRedux } from "@gtmhub/state-management";
import { getCurrentUserId } from "@gtmhub/users";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { ICollection } from "@webapp/core/core.models";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";
import { BadgeEvents } from "../badgeEvents";
import { IBadge, IBadgeAssignment } from "../models/badges";
import { AnnouncementService } from "../services/announcements.service";
import { BadgeService } from "../services/badges";
import { IBadgesModals } from "../services/badgesModals";

export interface IAwardBadgeScope extends IModalScope {
  badges: ICollection<IBadge>;
  badgeAssignment: IBadgeAssignment;
  selectedAssigneesIds: string[];
  selectedBadgeId: string;
  assignees: Record<string, Assignee>;
  indicators: { getBadges?: IIndicator; getAssignees?: IIndicator; assigning?: IIndicator };

  getAssignees(inputValue: string);
  selectAssignee(assigneeId: string);
  removeAssignee(assigneeId: string);
  selectBadge(id: string, badge: IBadge);
  assign();
  removeUserNotification(id: string);
}

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assignees: state.assignees.map,
});

export class AwardBadgeCtrl implements IStateInit {
  private selectedBadge: IBadge;
  private badgeEvents: BadgeEvents;
  private snackbarEvents: SnackbarEvents;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$q",
    "BadgeService",
    "AnnouncementService",
    "AssigneeActions",
    "$ngRedux",
    "SearchService",
    "BadgesModalsService",
    "badgeId",
    "assigneeId",
  ];

  constructor(
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $scope: IAwardBadgeScope,
    private $q: IQService,
    private badgeService: BadgeService,
    private announcementService: AnnouncementService,
    assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux,
    private searchService: SearchService,
    private badgesModalsService: IBadgesModals,
    private badgeId: string,
    private assigneeId: string
  ) {
    this.$ngRedux.dispatch(assigneeActions.getAssignees());
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.badgeEvents = new BadgeEvents(this.$scope);
    this.snackbarEvents = new SnackbarEvents(this.$scope);

    this.$scope.indicators = {};
    this.$scope.assign = this.assign;

    this.$scope.selectedAssigneesIds = [];
    this.$scope.selectBadge = this.selectBadge;
    this.$scope.getAssignees = this.getAssignees;
    this.$scope.selectAssignee = this.selectAssignee;
    this.$scope.removeAssignee = this.removeAssignee;

    this.$scope.badgeAssignment = {
      badgeId: null,
      assigneeIds: [],
      assignedById: getCurrentUserId(),
      comment: "",
      notificationStrategy: "custom",
      notify: [],
    };

    this.$scope.selectedBadgeId = this.badgeId;

    this.$scope.$on("usersSelected", this.onUsersSelected);
    this.$scope.removeUserNotification = this.removeUserNotification;

    return this.getMaps();
  };

  private getAssignees = (searchTerm: string): IPromise<Assignee[] | void> => {
    return this.$rootScope.traceAction("get_assignees", () => {
      this.$scope.indicators.getAssignees = { progress: true };
      const searchBody = {
        searchTerm,
        operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
        searchRequests: buildAssigneesSearchRequests("both", searchTerm),
      };

      return this.searchService.getSomeSearchData<Search<"teams"> | Search<"users">>(searchBody).then(
        (response) => {
          delete this.$scope.indicators.getAssignees;
          return searchItemsToAssignees(response.items);
        },
        (error) => {
          this.$scope.indicators.getAssignees = { error };
        }
      );
    });
  };

  private getMaps = () => {
    this.$scope.indicators.getBadges = { progress: true };

    let singleBadge: IPromise<IBadge>;
    let allBadges: IPromise<ICollection<IBadge>>;

    if (this.badgeId) {
      singleBadge = this.badgeService.getBadge(this.badgeId);
    } else {
      allBadges = this.badgeService.getBadges();
    }

    return this.$q
      .all({
        badge: singleBadge,
        badges: allBadges,
      })
      .then(
        (resolveMap) => {
          delete this.$scope.indicators.getBadges;

          if (resolveMap.badges) {
            this.$scope.badges = resolveMap.badges;
            this.$scope.badges.items.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
          }

          if (resolveMap.badge) {
            this.$scope.badges = { items: [], totalCount: 0 };
            this.$scope.badges.items.push(resolveMap.badge);
            this.$scope.badgeAssignment.badgeId = this.badgeId;
          }

          this.addSelectedTeamOrEmployee();
        },
        (error) => {
          this.$scope.indicators.getBadges = { error };
        }
      );
  };

  private selectAssignee = (assigneeId: string): void => {
    const isAssigneeAlreadySelected = this.$scope.selectedAssigneesIds.indexOf(assigneeId) > -1;
    if (isAssigneeAlreadySelected) {
      return;
    }

    this.$scope.selectedAssigneesIds.push(assigneeId);

    const isAssigneeOfTypeUser = this.$scope.assignees[assigneeId].type === "user";
    if (isAssigneeOfTypeUser && this.$scope.badgeAssignment.notify.indexOf(assigneeId) === -1) {
      this.$scope.badgeAssignment.notify.push(assigneeId);
    }
  };

  // if user is selected via employee, team or organization view
  //  add to selectedAssigneeIds
  private addSelectedTeamOrEmployee = () => {
    if (this.assigneeId) {
      this.$scope.selectedAssigneesIds.push(this.assigneeId);

      // if the selected assignee is a user --> add him to notify array
      if (this.$scope.assignees[this.assigneeId].type === "user") {
        this.$scope.badgeAssignment.notify.push(this.assigneeId);
      }
    }
  };

  private selectBadge = (id: string, b: IBadge) => {
    this.$scope.badgeAssignment.badgeId = id;
    this.selectedBadge = b;
  };

  private removeAssignee = (assigneeId: string) => {
    const assigneeIndex = this.$scope.selectedAssigneesIds.indexOf(assigneeId);
    if (assigneeIndex > -1) {
      this.$scope.selectedAssigneesIds.splice(assigneeIndex, 1);
    }

    const notifyIndex = this.$scope.badgeAssignment.notify.indexOf(assigneeId);
    if (notifyIndex > -1) {
      this.$scope.badgeAssignment.notify.splice(notifyIndex, 1);
    }
  };

  private assign = (): void => {
    this.$rootScope.traceAction("assign_badge", () => {
      this.$scope.badgeAssignment.assigneeIds = this.$scope.selectedAssigneesIds;
      // if sending to everyone or none --> pass an empty notify array
      this.$scope.badgeAssignment.notify = this.$scope.badgeAssignment.notificationStrategy === "custom" ? this.$scope.badgeAssignment.notify : [];

      this.$scope.indicators.assigning = { progress: true };
      this.badgeService.assignBadge(this.$scope.badgeAssignment).then(
        (newAssignment) => {
          if (newAssignment.status === "DIRECT_ASSIGN") {
            this.badgeEvents.broadcastBadgeAwardChanged({
              action: "assigned",
              assignment: newAssignment,
              badge: this.selectedBadge,
            });

            const announcement = this.badgeService.configureRewardAnnouncement(newAssignment);
            this.announcementService.post(announcement).then(
              () => {
                delete this.$scope.indicators.assigning;
                this.$rootScope.$broadcast("employeeChanged", true);
                this.$rootScope.$broadcast("employeesChanged", true);
                this.$scope.$close();

                this.badgesModalsService.openBadgeDialog(newAssignment.id);
              },
              (err) => (this.$scope.indicators.assigning = { error: err })
            );
          }

          if (newAssignment.status === "PENDING_ASSIGN") {
            delete this.$scope.indicators.assigning;
            this.$scope.$close();
            this.snackbarEvents.broadcastShowSnackbar("sent_for_approval", { customDuration: 4000 });
          }
        },
        (error) => {
          this.$scope.indicators.assigning = { error };
        }
      );
    });
  };

  private removeUserNotification = (id: string) => {
    this.$scope.badgeAssignment.notify.splice(this.$scope.badgeAssignment.notify.indexOf(id), 1);
  };

  private onUsersSelected = (ev, users) => {
    for (let i = 0; i < users.length; i++) {
      if (this.$scope.badgeAssignment.notify.indexOf(users[i]) === -1) {
        this.$scope.badgeAssignment.notify.push(users[i]);
      }
    }
  };
}
